import { Area, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import '@Pages/GeoMMM/Results/Components/ActualVsPredicted.scss';
import { Brand60, Neutral400 } from '@src/utils/colors';
import { dateToMddyy } from '../../../../utils/date-utils';
import { DateGroupedTooltipContent } from './tooltip-utils';

interface ActualVsPredictedProps {
  data: any[];
  dateGrouping: string;
  legendValue: string;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
}

export const ActualVsPredicted = ({
  data,
  dateGrouping,
  legendValue,
  syncId,
  tooltipFormatter,
  yAxisFormatter
}: ActualVsPredictedProps) => {
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data
  });

  return (
    <div className='geo-actual-vs-predicted-wrapper'>
      <div className='geo-actual-vs-predicted-graph'>
        <AutoSizer
          renderProp={({ width, height }) => (
            <ComposedChart
              data={data}
              height={height}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              syncId={syncId}
              ref={setChartRef}
              width={width}
            >
              <XAxis dataKey='date' tick={TICK_STYLE} tickFormatter={val => dateToMddyy(val)} />
              <YAxis
                tick={TICK_STYLE}
                tickFormatter={yAxisFormatter}
                tickLine={false}
                axisLine={false}
                width={yAxisWidth}
              />
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <Area
                dataKey='actual'
                fill={Neutral400}
                isAnimationActive={false}
                stroke={Neutral400}
                type='monotone'
              />
              <Line
                dataKey='expected'
                dot={false}
                isAnimationActive={false}
                stroke={Brand60}
                strokeWidth={3}
                type='monotone'
              />
              <Tooltip
                content={
                  <DateGroupedTooltipContent
                    dateGrouping={dateGrouping}
                    // TODO: Ask Econometrics to update column name from "expected" to "predicted"
                    getLabel={item => (item.name === 'expected' ? 'Predicted' : 'Actual')}
                    formatValue={value => (tooltipFormatter ? tooltipFormatter(value) : value)}
                  />
                }
                isAnimationActive={false}
              />
            </ComposedChart>
          )}
        />
      </div>
      <div className='right-of-chart'>
        <div className='legend'>
          <div className='legend-item'>
            <div className='square' style={{ backgroundColor: Neutral400 }} />
            <div>{`Actual ${legendValue}`}</div>
          </div>
          <div className='legend-item'>
            <div className='line' />
            <div>{`Predicted ${legendValue}`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
